import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useFees } from '../../context/FeeContext';
import BookingCharges from './BookingCharges';
import ServiceTracking from './ServiceTracking';
import DisputeProgressBadge from '../DisputeProgressBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SimpleBookingDetailsModal = ({ booking, onClose, effectiveRole }) => {
  const { getBookingPricing } = useFees();
  const navigate = useNavigate();

  const [disputeObj, setDisputeObj] = useState(null);
  useEffect(() => {
    const did = booking?.dispute_id;
    if (did && (booking?.status === 'disputed' || booking?.was_disputed)) {
      axios.get(`${API}/disputes/${did}`).then((r) => setDisputeObj(r.data)).catch(() => setDisputeObj(null));
    } else {
      setDisputeObj(null);
    }
  }, [booking?.dispute_id, booking?.status, booking?.was_disputed]);

  if (!booking) return null;
  
  // Unpaid bookings use the CURRENT live platform fee; paid bookings use the
  // locked breakdown stored on the booking (see FeeContext.getBookingPricing).
  const pricing = getBookingPricing(booking);
  const bookedClientFeeRate = pricing.clientFeeRate;
  const bookedProviderFeeRate = pricing.providerFeeRate;
  const serviceAmount = pricing.serviceAmount;
  const clientFee = pricing.clientFee;
  const providerFee = pricing.providerFee;
  const clientTotal = pricing.clientTotal;
  const providerPayout = pricing.providerPayout;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'accepted': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'cancelled':
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'completion_requested': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'disputed': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-2xl sticky top-0">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <h3 className="font-bold text-lg">Booking Details</h3>
              <p className="text-sm text-blue-100">{booking.service_title}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition"
              data-testid="close-booking-details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Order Code and Status Badge */}
          <div className="flex justify-center gap-3 flex-wrap">
            {/* Booking Order Code for service bookings */}
            {booking.booking_order_code && (
              <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm px-3 py-1.5 rounded-full font-mono font-semibold" title="Service Booking Code">
                #SE-{booking.booking_order_code}
              </span>
            )}
            {/* Job Order Code for job-based bookings */}
            {booking.type === 'job' && booking.job_order_code && (
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm px-3 py-1.5 rounded-full font-mono font-semibold" title="Job Order Code">
                #JO-{booking.job_order_code}
              </span>
            )}
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(booking.status)}`}>
              {booking.status?.replace(/_/g, ' ').toUpperCase()}
            </span>
            {booking.was_disputed && booking.status === 'completed' && (
              <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700 border border-orange-300" data-testid="was-disputed-badge">
                Was Disputed
              </span>
            )}
          </div>

          {/* Service Information */}
          <div className="bg-gray-50 rounded-xl p-4 border">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Service Information
            </h4>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium text-gray-600">Service:</span> {booking.service_title}</p>
              {effectiveRole === 'provider' ? (
                <p><span className="font-medium text-gray-600">Client:</span> {booking.client_name}</p>
              ) : (
                <p><span className="font-medium text-gray-600">Provider:</span> {booking.provider_name}</p>
              )}
            </div>
          </div>

          {/* Schedule Information */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Schedule
            </h4>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium text-gray-600">Date:</span> {formatDate(booking.scheduled_date)}</p>
              {booking.scheduled_time && (
                <p><span className="font-medium text-gray-600">Time:</span> {formatTime(booking.scheduled_time)}</p>
              )}
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pricing
            </h4>
            <div className="space-y-2">
              {(booking.final_amount || booking.price) && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Amount:</span>
                    <span className="font-medium">₱{(booking.final_amount || booking.price)?.toLocaleString()}</span>
                  </div>
                  
                  {effectiveRole === 'provider' ? (
                    <>
                      <div className="flex justify-between text-sm text-red-600">
                        <span>Platform Fee ({bookedProviderFeeRate}%):</span>
                        <span>-₱{providerFee?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-purple-200">
                        <span>Your Earnings:</span>
                        <span className="text-green-600">
                          ₱{providerPayout?.toLocaleString()}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm text-orange-600">
                        <span>Platform Fee ({bookedClientFeeRate}%):</span>
                        <span>+₱{clientFee?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-purple-200">
                        <span>Total Paid:</span>
                        <span className="text-purple-700">
                          ₱{clientTotal?.toLocaleString()}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Consolidated Service Location & Tracking: single collapsible map
              (static pin when idle, live route + ETA when on the way), with
              status time and Open-in-Google-Maps. Pin editing lives in Account
              Settings → Saved Locations, so it is intentionally not here. */}
          <ServiceTracking
            booking={booking}
            effectiveRole={effectiveRole}
            showLocationMap={true}
            collapsible={true}
            address={effectiveRole === 'provider'
              ? (booking.location || null)
              : (booking.provider_area || null)}
          />

          {/* Extra charges (provider add-ons) & tips */}
          <BookingCharges booking={booking} effectiveRole={effectiveRole} />

          {/* Client Notes/Instructions */}
          {booking.notes && (
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Notes
              </h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{booking.notes}</p>
            </div>
          )}

          {/* Completion Proof (images & notes from provider) */}
          {(booking.proof_images?.length > 0 || booking.completion_notes) && (
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-testid="completion-proof-section">
              <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Provider's Completion Proof
              </h4>
              {booking.completion_notes && (
                <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{booking.completion_notes}</p>
              )}
              {booking.proof_images?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {booking.proof_images.map((img, idx) => (
                    <a
                      key={idx}
                      href={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg overflow-hidden border border-blue-200 hover:border-blue-400 transition"
                      data-testid={`proof-image-${idx}`}
                    >
                      <img
                        src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                        alt={`Proof ${idx + 1}`}
                        className="w-full h-24 object-cover"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Dispute History (for resolved disputes) */}
          {booking.was_disputed && booking.status === 'completed' && (
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200" data-testid="dispute-history-section">
              <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Dispute History
              </h4>
              <p className="text-sm text-gray-700">
                This booking was previously disputed and resolved
                {booking.dispute_resolution && ` (${booking.dispute_resolution.replace(/_/g, ' ')})`}.
              </p>
              {disputeObj && (
                <div className="mt-3 bg-white/70 rounded-lg p-3 border border-orange-200">
                  <DisputeProgressBadge dispute={disputeObj} />
                </div>
              )}
              {booking.dispute_id && (
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/disputes/${booking.dispute_id}`);
                  }}
                  className="mt-2 text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                  data-testid="view-dispute-history-btn"
                >
                  View Dispute Details
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Dispute Alert */}
          {booking.status === 'disputed' && (
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Dispute Active
              </h4>
              <p className="text-sm text-red-700 mb-3">
                {effectiveRole === 'provider' 
                  ? 'The client has filed a dispute for this booking. Please respond to resolve the issue.'
                  : 'You have filed a dispute for this booking. Track the progress and communicate with the provider.'}
              </p>
              {disputeObj && (
                <div className="mb-3 bg-white/70 rounded-lg p-3 border border-red-200">
                  <DisputeProgressBadge dispute={disputeObj} />
                </div>
              )}
              <button
                onClick={() => {
                  onClose();
                  navigate(`/disputes/${booking.dispute_id}`);
                }}
                className="w-full py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition flex items-center justify-center gap-2"
                data-testid="view-dispute-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {effectiveRole === 'provider' ? 'Respond to Dispute' : 'View Dispute'}
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
            >
              Close
            </button>
            {booking.status === 'accepted' && effectiveRole === 'client' && booking.payment_status !== 'paid' && (
              <Link
                to={`/payment/${booking.id}`}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition text-center"
                data-testid="booking-pay-btn"
              >
                {booking.payment_status ? '🔁 Retry Payment' : 'Pay Now'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleBookingDetailsModal;
